import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Button,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import ColumnSettingsDialog from "./GridColumnSettingsPopup";
import { ExecuteLayoutEventMethods } from "../../General/commonFunctions";

const GridLayoutSettingsDialog = ({
  open,
  onClose,
  currentGridProps,
  setCurrentGridProps,
  setGridCols,
  layoutkey,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [columns, setColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState({});
  const [openColumnSettings, setOpenColumnSettings] = useState(false);

  useEffect(() => {
    setColumns(currentGridProps?.props?.columnspecs || []);
  }, [currentGridProps]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const getUpdatedColumn = (updatedColumn) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.Data === updatedColumn.Data ? { ...col, ...updatedColumn } : col
      )
    );
  };

  const handleColVisiblity = (e, index) => {
    const updated = [...columns];
    updated[index].visible = e.target.checked ? 1 : 0;
    setColumns(updated);
  };

  const updateColumnValue = (index, key, value) => {
    setColumns((prev) =>
      prev.map((col, i) => (i === index ? { ...col, [key]: value } : col))
    );
  };

  const SaveGridLayoutSettings = async () => {
    setGridCols(columns);
    setCurrentGridProps((prev) => ({
      ...prev,
      props: { ...prev.props, columnspecs: columns },
    }));

    const {
      whenrowdblclick,
      whenfocusedrowchanged,
      whenselectionchange,
      layoutkey,
      watch,
      ...cleanProps
    } = currentGridProps.props;

    const layoutSettings = {
      ...currentGridProps,
      props: { ...cleanProps, columnspecs: columns },
    };

    await ExecuteLayoutEventMethods(
      [
        {
          exec: "filldataset",
          args: {
            proc: "PWA.dsSaveListView",
            args: {
              layoutkey: layoutkey,
              layoutjson: JSON.stringify(layoutSettings),
            },
          },
        },
      ],
      {}
    );

    onClose();
  };

  return (
    <>
      <ColumnSettingsDialog
        open={openColumnSettings}
        onClose={() => setOpenColumnSettings(false)}
        selectedColumn={selectedColumn}
        onOk={getUpdatedColumn}
      />

      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        {/* ---------- TITLE ---------- */}
        <DialogTitle sx={{ p: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Grid Layout Settings</Typography>

            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* ---------- CONTENT (SCROLLABLE) ---------- */}
        <DialogContent
          dividers
          sx={{
            p: 2,
            maxHeight: "calc(100vh - 180px)",
            overflowY: "auto",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 1, minHeight: 32 }}
          >
            <Tab label="General" sx={{ minHeight: 32, p: 0.5 }} />
          </Tabs>

          {tabValue === 0 && (
            <TableContainer
              component={Paper}
              sx={{ border: "1px solid #e0e0e0" }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      Field Name
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      Caption
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      Width(px)
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      Format
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      Position
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      Visible
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                      ...
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {columns.map((field, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {field.Data}
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          value={field.Caption}
                          onChange={(e) =>
                            updateColumnValue(index, "Caption", e.target.value)
                          }
                          fullWidth
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          value={field.Width?.replace("px", "") ?? ""}
                          onChange={(e) =>
                            updateColumnValue(index, "Width", e.target.value)
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>Format</InputLabel>
                          <Select
                            value={field.format}
                            label="Format"
                            onChange={(e) =>
                              updateColumnValue(index, "format", e.target.value)
                            }
                          >
                            <MenuItem value="String (No format set)">
                              String (No format set)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={field.visibleIndex}
                          onChange={(e) =>
                            updateColumnValue(
                              index,
                              "visibleIndex",
                              e.target.value
                            )
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={field.visible === 1}
                          onChange={(e) => handleColVisiblity(e, index)}
                        />
                      </TableCell>

                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedColumn(field);
                          setOpenColumnSettings(true);
                        }}
                      >
                        ...
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        {/* ---------- FOOTER (FIXED) ---------- */}
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={SaveGridLayoutSettings}
          >
            Save
          </Button>
          {/* <Button size="small" onClick={onClose}>
            Close
          </Button> */}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GridLayoutSettingsDialog;
